import { ComponentType, FocusEvent, useCallback, useState } from 'react';
import { Validator } from '../../utils/validators/types';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { ControlProps } from './makeTextControl';

interface ControlPropsWithValidate<TElement> extends ControlProps<TElement> {
    validator?: Validator<string>;
}

export function makeTextControlValidate<TElement extends HTMLInputElement>(
    Component: ComponentType<ControlProps<TElement>>,
) {
    return (props: ControlPropsWithValidate<TElement>) => {
        const [error, setError] = useState<string | undefined>();
        const { validator } = props;

        const onBlur = useCallback(
            (event: FocusEvent<TElement>) => {
                props.onBlur?.(event);

                if (validator) {
                    setError(validator(event.currentTarget.value));
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [props.onBlur, validator],
        );

        const onInput = useCallback(
            (event: FocusEvent<TElement>) => {
                props.onInput?.(event);

                if (validator && !validator(event.currentTarget.value)) {
                    setError(undefined);
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [props.onInput, validator],
        );

        return (
            <Flex width="100%">
                <Component {...props} error={Boolean(error)} onBlur={onBlur} onInput={onInput} />
                {error ? (
                    <Typography variant="caption" color="error" mt={1}>
                        {error}
                    </Typography>
                ) : null}
            </Flex>
        );
    };
}
